import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import styles from "./CategoryListItem.module.css";
import EditButton from "components/EditButton";
import IconButton from "components/IconButton";
import { ChevronRight } from "react-ikonate";
import addIcon from "../../assets/add.svg";
import editIcon from "../../assets/EditListIcon.svg";
import TextInput from "components/TextInput";
import Button from "Shared/Button";
import { useTranslation } from "react-i18next";

const CheckIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.7803 4.21967C14.0732 4.51256 14.0732 4.98744 13.7803 5.28033L6.78033 12.2803C6.48744 12.5732 6.01256 12.5732 5.71967 12.2803L2.21967 8.78033C1.92678 8.48744 1.92678 8.01256 2.21967 7.71967C2.51256 7.42678 2.98744 7.42678 3.28033 7.71967L6.25 10.6893L12.7197 4.21967C13.0126 3.92678 13.4874 3.92678 13.7803 4.21967Z"
      fill="#2E7D32"
    />
  </svg>
);

const AddCircleIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99999 0.727273C9.4384 0.727273 10.8445 1.15381 12.0405 1.95295C13.2365 2.75209 14.1687 3.88793 14.7191 5.21684C15.2696 6.54576 15.4136 8.00807 15.133 9.41884C14.8523 10.8296 14.1597 12.1255 13.1426 13.1426C12.1255 14.1597 10.8296 14.8524 9.41885 15.133C8.00808 15.4136 6.54576 15.2696 5.21685 14.7191C3.88793 14.1687 2.75209 13.2365 1.95296 12.0405C1.15382 10.8445 0.727262 9.43841 0.727262 8C0.72944 6.07183 1.49637 4.22323 2.8598 2.85981C4.22322 1.49638 6.07182 0.729451 7.99999 0.727273ZM7.99999 0C6.41774 0 4.87102 0.4692 3.55542 1.34825C2.23983 2.2273 1.21448 3.47672 0.608975 4.93853C0.00347379 6.40034 -0.154963 8.00887 0.153719 9.56072C0.4624 11.1126 1.22432 12.538 2.34314 13.6568C3.46196 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6517 12.4446C15.5308 11.129 16 9.58226 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842856 10.1217 0 7.99999 0Z"
      fill="#243746"
    />
    <path
      d="M12.0009 7.63685H8.3645V4.00049H7.6393V7.63685H4.00293V8.36413H7.6393V12.0005H8.36657V8.36413H12.0029L12.0009 7.63685Z"
      fill="#243746"
    />
  </svg>
);

export function CategoryListItem({
  onClick,
  isNew, // ✅ isNew === true => user-created list
  isModal,
  isInModal,
  category,
  onDelete,
  onSave,
  onChevronClick,
}) {
  const { t } = useTranslation();
  const { name } = category;
  const history = useHistory();
  const canNavigate = typeof onChevronClick === "function";
  const isSystemList = Boolean(category?.isSystemList);
  const isAlreadyInList = isInModal && Boolean(category?.isAlreadyInList);
  const showCustomEditIcon = !isInModal && canNavigate && !isSystemList;

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);

  const handleChevronClick = () => onChevronClick?.(category);

  // The item is already in this list; take the user straight to the list
  // detail view (My Lists -> SavedListPage) instead of re-opening the add
  // flow, which would otherwise silently no-op on the backend.
  const navigateToListDetails = () => {
    const listId = category?.listId ?? category?.setId;
    if (listId == null) return;
    history.push({ pathname: `/my-lists/${listId}`, state: { listId } });
  };

  const handleRowClick = () => {
    if (isEditing) return;
    if (isInModal) {
      if (isAlreadyInList) {
        navigateToListDetails();
        return;
      }
      onSave?.(category);
      return;
    }
    onClick?.(category);
  };
  const handleEditClick = () => {
    if (isInModal) {
      if (isAlreadyInList) {
        navigateToListDetails();
        return;
      }
      onSave?.(category);
      return;
    }
    if (isSystemList) return;
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (isSystemList) return;
    category.name = editedName;
    setIsEditing(false);
    onSave?.({ ...category, name: editedName });
  };

  const handleCancelClick = () => {
    setEditedName(name);
    setIsEditing(false);
  };

  const handleDeleteClick = () => onDelete(category.listId ?? category.setId);

  return (
    <div
      className={[
        styles.container,
        isEditing ? styles.isEditing : "",
        isInModal
          ? styles.inModal
          : isSystemList
          ? styles.presetList
          : styles.userList,
        isAlreadyInList ? styles.alreadyInList : "",
      ].join(" ")}
      onClick={handleRowClick}
      role={canNavigate && !isEditing ? "button" : undefined}
      tabIndex={canNavigate && !isEditing ? 0 : undefined}
      onKeyDown={(e) => {
        if (!canNavigate || isEditing) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      {isEditing ? (
        <div className={styles.textInputContainer}>
          <TextInput
            label={t("New List")}
            placeholder={t("Create New List")}
            value={editedName}
            setValue={(e) => setEditedName(e.target.value)}
          />
          <div className={styles.buttonsContainer}>
            <div className={styles.leftButtonsContainer}>
              <Button
                text={t("Delete")}
                className="deleteBtn"
                onClick={handleDeleteClick}
              />
            </div>
            <div className={styles.rightButtonsContainer}>
              <Button
                text={t("Cancel")}
                onClick={handleCancelClick}
                className="cancelBtn"
              />
              <Button
                text={t("Save")}
                onClick={handleSaveClick}
                className="saveBtn"
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.categoryName}>{t(name)}</div>
          <div className={styles.iconContainer}>
            {isAlreadyInList ? (
              <span
                className={styles.checkIcon}
                aria-label={t("Already added")}
              >
                {CheckIcon}
              </span>
            ) : (
              <>
                {isNew && !isModal && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    icon={addIcon}
                    iconNode={AddCircleIcon}
                    label={t("Add")}
                    className={`${styles.iconButtonIcon} ${styles.iconButtonText}`}
                  />
                )}
                {isNew && isModal && (
                  <EditButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    label={t("Edit")}
                    className={`${styles.iconButtonIcon} ${styles.iconButtonText}`}
                  />
                )}
              </>
            )}
            <div className={styles.pointer}>
              {canNavigate && (
                <div className={styles.navIcons}>
                  {showCustomEditIcon && (
                    <img
                      src={editIcon}
                      alt="Edit list"
                      className={styles.genericEditIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick();
                      }}
                    />
                  )}
                  <ChevronRight
                    className={styles.chevronIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChevronClick();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

CategoryListItem.propTypes = {
  onClick: PropTypes.func,
  isNew: PropTypes.bool,
  isModal: PropTypes.bool,
  category: PropTypes.shape({
    name: PropTypes.string,
    listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stocks: PropTypes.arrayOf(
      PropTypes.shape({
        stockId: PropTypes.number,
        order: PropTypes.number,
      })
    ),
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSystemList: PropTypes.bool,
    isNavigable: PropTypes.bool,
    isAlreadyInList: PropTypes.bool,
  }),
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  onChevronClick: PropTypes.func,
};

export default CategoryListItem;
